import Vue from "vue";
import VueRouter from "vue-router";
import VueMeta from "vue-meta";

import store from "@/store";
import entryCache from "@/libs/entry";

import Home from "@/views/Home";

Vue.use(VueRouter);
Vue.use(VueMeta);

entryCache.mounted();

export const routes = [
    {
        path: "/",
        name: "Home",
        component: Home
    },
    {
        path: "/blog",
        name: "Blog",
        component: () => import(/* webpackChunkName: "blog" */ "../views/Blog.vue"),
        props: true,
        beforeEnter(routeTo, routeFrom, next) {
            store.dispatch("blog/medium").then(() => {
                routeTo.params.feed = store.getters["blog/getFeed"];
                routeTo.params.items = store.getters["blog/getItems"];
                next();
            });
        }
    },
    {
        path: "/soy-organizacion",
        component: () => import(/* webpackChunkName: "blog" */ "../views/SoyOrganizacion.vue")
    },
    {
        path: "/organizations",
        alias: "/organizaciones",
        name: "Organizations",
        component: () =>
            import(/* webpackChunkName: "organizations" */ "../views/Organizations.vue")
    },
    {
        path: "/services",
        alias: "/servicios",
        name: "Servicios",
        component: () =>
            import(/* webpackChunkName: "services" */ "../views/Services.vue")
    },
    {
        path: "/about",
        alias: "/sobre-nosotros",
        name: "About",
        component: () => import(/* webpackChunkName: "about" */ "../views/About.vue"),
        meta: {
            sitemap: {
                ignoreRoute: true
            }
        }
    },
    {
        path: "/offers",
        alias: "/ofertas",
        name: "Offers",
        component: () => import(/* webpackChunkName: "offers" */ "../views/Offers.vue"),
        props: true
    },
    {
        path: "/offer/:slug",
        alias: "/oferta/:slug",
        name: "OfferShow",
        component: () => import(/* webpackChunkName: "offerShow" */ "../views/OfferShow.vue"),
        meta: {
            sitemap: {
                ignoreRoute: true
            }
        },
        props: true,
        beforeEnter(routeTo, routeFrom, next) {
            store.dispatch("offers/get", routeTo.params.slug).then(offer => {
                routeTo.params.offer = offer;
                next();
            });
        }
    },
    {
        path: "/postulate/:slug",
        alias: "/postular/:slug",
        name: "Postulate",
        component: () => import(/* webpackChunkName: "postulate" */ "../views/Postulate.vue"),
        meta: {
            authenticatedAs: "candidate",
            sitemap: {
                ignoreRoute: true
            }
        },
        props: true,
        beforeEnter(routeTo, routeFrom, next) {
            // Se debe validar si el candidato ya postuló a la oferta
            if (routeFrom.path != "/candidate/profile" && !routeTo.params.status) {
                entryCache.set({
                    url: routeTo.path
                });
                next("/candidate/profile");
            }
            store.dispatch("offers/get", routeTo.params.slug).then(offer => {
                routeTo.params.offer = offer;
                store.dispatch("users/postulations").then(() => {
                    routeTo.params.posts = store.getters["users/getPostulations"];
                    next();
                });
            });
        }
    },
    {
        path: "/postulate/success/:slug",
        alias: "/postular/exito/:slug",
        name: "PostulateSuccess",
        component: () =>
            import(/* webpackChunkName: "postulate" */ "../views/PostulateSuccess.vue"),
        meta: {
            authenticatedAs: "candidate",
            sitemap: {
                ignoreRoute: true
            }
        },
        props: true
    },
    {
        path: "/login",
        alias: "/iniciar-sesion",
        name: "Login",
        component: () => import(/* webpackChunkName: "login" */ "../views/Login.vue"),
        props: true
    },
    {
        path: "/logout",
        alias: "/cerrar-sesion",
        name: "Logout",
        props: true,
        beforeEnter(routeTo, routeFrom, next) {
            store.dispatch("users/logout");
            store.dispatch("cookies/activity", {
                path: routeTo.path,
                activity: "logout"
            });
            entryCache.clear();
            router.push({ path: "/login" });
        }
    },
    {
        path: "/organization/profile",
        alias: "/organizacion/perfil",
        name: "OrganizationProfile",
        component: () =>
            import(
                /* webpackChunkName: "organizationProfile" */ "../views/OrganizationProfile.vue"
            ),
        meta: {
            authenticatedAs: "organization",
            sitemap: {
                ignoreRoute: true
            }
        },
        props: true
    },
    {
        path: "/organization/profile/:slug",
        alias: "/organizacion/perfil/:slug",
        name: "OrganizationHome",
        component: () =>
            import(/* webpackChunkName: "organizationHome" */ "../views/OrganizationHome.vue"),
        meta: {
            sitemap: {
                ignoreRoute: true
            }
        },
        props: true,
        beforeEnter(routeTo, routeFrom, next) {
            store.dispatch("users/getOrg", routeTo.params.slug).then(org => {
                routeTo.params.organization = org.organization;
                next();
            });
        }
    },
    {
        path: "/organization/users",
        alias: "/organizacion/usuarios",
        name: "OrganizationUsers",
        component: () =>
            import(/* webpackChunkName: "OrganizationUsers" */ "../views/OrganizationUsers.vue"),
        meta: {
            authenticatedAs: "organization",
            roleAs: "admin",
            sitemap: {
                ignoreRoute: true
            }
        },
        props: true,
        beforeEnter(routeTo, routeFrom, next) {
            store.dispatch("users/list").then(users => {
                routeTo.params.users = users;
                next();
            });
        }
    },
    {
        path: "/organization/payments",
        alias: "/organizacion/pagos",
        name: "OrganizationPayments",
        component: () =>
            import(
                /* webpackChunkName: "OrganizationPayments" */ "../views/OrganizationPayments.vue"
            ),
        meta: {
            authenticatedAs: "organization",
            roleAs: "admin",
            sitemap: {
                ignoreRoute: true
            }
        }
    },
    {
        path: "/sysadmin/home",
        name: "AdminHome",
        component: () =>
            import(
                /* webpackChunkName: "organizationDashboard" */ "../views/OrganizationDashboard.vue"
            ),
        meta: {
            authenticatedAs: "sysadmin",
            roleAs: "sysadmin",
            sitemap: {
                ignoreRoute: true
            }
        },
        props: true,
        beforeEnter(routeTo, routeFrom, next) {
            next();
        }
    },
    {
        path: "/sysadmin/admin/offers-organizations",
        name: "AdminOrgsOffers",
        component: () =>
            import(/* webpackChunkName: "moderation" */ "../views/AdminOrgsOffers.vue"),
        meta: {
            authenticatedAs: "sysadmin",
            roleAs: "sysadmin",
            sitemap: {
                ignoreRoute: true
            }
        },
        props: true
    },
    {
        path: "/sysadmin/admin/discount",
        name: "AdminDiscount",
        component: () => import(/* webpackChunkName: "moderation" */ "../views/AdminDiscount.vue"),
        meta: {
            authenticatedAs: "sysadmin",
            roleAs: "sysadmin",
            sitemap: {
                ignoreRoute: true
            }
        },
        props: true
    },
    {
        path: "/sysadmin/admin/payments",
        name: "AdminPayments",
        component: () => import(/* webpackChunkName: "moderation" */ "../views/AdminPayments.vue"),
        meta: {
            authenticatedAs: "sysadmin",
            roleAs: "sysadmin",
            sitemap: {
                ignoreRoute: true
            }
        },
        props: true
    },
    {
        path: "/sysadmin/admin/users",
        name: "AdminUsers",
        component: () => import(/* webpackChunkName: "moderation" */ "../views/AdminUsers.vue"),
        meta: {
            authenticatedAs: "sysadmin",
            roleAs: "sysadmin",
            sitemap: {
                ignoreRoute: true
            }
        },
        props: true
    },
    {
        path: "/sysadmin/moderation",
        name: "AdminModeration",
        component: () => import(/* webpackChunkName: "moderation" */ "../views/Moderations.vue"),
        meta: {
            authenticatedAs: "sysadmin",
            roleAs: "sysadmin",
            sitemap: {
                ignoreRoute: true
            }
        },
        props: true,
        beforeEnter(routeTo, routeFrom, next) {
            next();
        }
    },
    {
        path: "/candidate/profile",
        alias: "/candidato/perfil",
        name: "CandidateProfile",
        component: () =>
            import(/* webpackChunkName: "candidateProfile" */ "../views/CandidateProfile.vue"),
        meta: {
            authenticatedAs: "candidate",
            sitemap: {
                ignoreRoute: true
            }
        },
        props: true
    },
    {
        path: "/candidate/home",
        alias: "/candidato/inicio",
        name: "CandidateDashboard",
        component: () =>
            import(/* webpackChunkName: "candidateDashboard" */ "../views/CandidateDashboard.vue"),
        meta: { authenticatedAs: "candidate" },
        props: true,
        beforeEnter(routeTo, routeFrom, next) {
            store.dispatch("users/postulations").then(() => {
                routeTo.params.posts = store.getters["users/getPostulations"];
                next();
            });
        }
    },
    {
        path: "/organization/home",
        alias: "/organizacion/inicio",
        name: "OrganizationDashboard",
        component: () =>
            import(
                /* webpackChunkName: "organizationDashboard" */ "../views/OrganizationDashboard.vue"
            ),
        meta: {
            authenticatedAs: "organization",
            sitemap: {
                ignoreRoute: true
            }
        },
        props: true,
        beforeEnter(routeTo, routeFrom, next) {
            next();
        }
    },
    {
        path: "/organization/offer/:slug",
        alias: "/organizacion/oferta/:slug",
        name: "OfferDashboard",
        component: () =>
            import(/* webpackChunkName: "offerDashboard" */ "../views/OfferDashboard.vue"),
        meta: {
            authenticatedAs: "organization",
            sitemap: {
                ignoreRoute: true
            }
        },
        props: true,
        beforeEnter(routeTo, routeFrom, next) {
            store.dispatch("offers/get", routeTo.params.slug).then(response => {
                routeTo.params.offer = response;
                next();
            });
            /// TODO CATCH !!!
        }
    },
    {
        path: "/pricing",
        alias: "/planes",
        name: "Pricing",
        component: () => import(/* webpackChunkName: "pricing" */ "../views/Pricing.vue")
    },
    {
        path: "/checkout",
        alias: "/verificar-compra",
        component: () => import(/* webpackChunkName: "checkoutPage" */ "../views/CheckoutPage.vue"),
        name: "Checkout",
        props: route => ({ pack: route.query.pack }),
        meta: {
            authenticatedAs: "organization",
            sitemap: {
                ignoreRoute: true
            }
        },
        beforeEnter(routeTo, routeFrom, next) {
            let entry = entryCache.get();
            let redirect =
                (entry && entry.params.redirect) ||
                routeTo.params.redirect ||
                routeTo.query.redirect;
            entryCache.clear();
            if (redirect) {
                window.location.href = redirect;
            } else {
                next();
            }
        }
    },
    {
        path: "/payment-complete",
        alias: "/pago-completo",
        component: () =>
            import(/* webpackChunkName: "paymentComplete" */ "../views/PaymentComplete.vue"),
        name: "PaymentComplete",
        props: true,
        meta: {
            authenticatedAs: "organization",
            sitemap: {
                ignoreRoute: true
            }
        }
    },
    {
        path: "/checkout/pending",
        alias: "/verificar-compra/pendiente",
        name: "CheckoutPending",
        props: true,
        meta: {
            authenticatedAs: "organization",
            sitemap: {
                ignoreRoute: true
            }
        },
        beforeEnter(routeTo, routeFrom, next) {
            const payment = routeTo.query || entry.query;
            /*
            store.dispatch("payments/registerPayment", payment).then(res => {
                entryCache.clear();
                next("/payment-complete");
            }).catch(err => {
                routeTo.params.payment = {status: false};
                entryCache.clear();
                next("/payment-complete");
            });*/
        }
    },
    {
        path: "/checkout/success",
        alias: "/verificar-compra/exito",
        name: "CheckoutSuccess",
        props: true,
        meta: {
            authenticatedAs: "organization",
            sitemap: {
                ignoreRoute: true
            }
        },
        beforeEnter(routeTo, routeFrom, next) {
            store.commit("payments/SET_COMPLETED_PAYMENT", true);
            next("/payment-complete");
        }
    },
    {
        path: "/checkout/failure",
        alias: "/verificar-compra/fallido",
        name: "CheckoutFailure",
        props: true,
        meta: {
            authenticatedAs: "organization",
            sitemap: {
                ignoreRoute: true
            }
        },
        beforeEnter(routeTo, routeFrom, next) {
            store.commit("payments/SET_COMPLETED_PAYMENT", false);
            next("/payment-complete");
        }
    },
    {
        /// TODO esta ruta es temporall ,debiese elimianrse ///
        path: "/checkout/offline/successful",
        alias: "/verificar-compra/sin-conexion/exitoso",
        name: "CheckoutOfflineSuccessful",
        component: () => import(/* webpackChunkName: "services" */ "../views/OfflinePayment.vue"),
        props: true,
        meta: {
            sitemap: {
                ignoreRoute: true
            }
        }
    },
    {
        path: "/offer/edit/:slug",
        alias: "/oferta/editar/:slug",
        name: "OfferEdit",
        component: () => import(/* webpackChunkName: "offerEdit" */ "../views/OfferEdit.vue"),
        props: true,
        meta: {
            authenticatedAs: "organization"
        },
        beforeEnter(to, from, next) {
            store.dispatch("offers/get", to.params.slug).then(response => {
                to.params.offer = response;
                next();
            });
        }
    },
    {
        path: "/offers/post",
        alias: "/ofertas/publicar",
        name: "OfferPost",
        component: () => import(/* webpackChunkName: "offerPost" */ "../views/OfferPost.vue"),
        meta: {
            authenticatedAs: "organization",
            sitemap: {
                ignoreRoute: true
            }
        }
    },
    {
        path: "/password/:type/:token",
        alias: "/contrasena/:type/:token",
        name: "UpdatePassword",
        component: () =>
            import(/* webpackChunkName: "updatePassword" */ "../views/UpdatePassword.vue"),
        meta: {
            sitemap: {
                ignoreRoute: true
            }
        },
        props: true
    },
    {
        path: "/password/forget",
        alias: "/contrasena/olvidada",
        name: "ForgetPassword",
        component: () =>
            import(/* webpackChunkName: "forgetPassword" */ "../views/ForgetPassword.vue"),
        props: true
    },
    {
        path: "/register",
        alias: "/registro",
        name: "Register",
        component: () => import(/* webpackChunkName: "register" */ "../views/Register.vue")
    },
    {
        path: "/register/:type",
        alias: "/registro/:type",
        name: "RegisterType",
        component: () => import(/* webpackChunkName: "register" */ "../views/RegisterType.vue"),
        meta: {
            sitemap: {
                slugs: ["person", "organization"]
            }
        },
        props: true
    },
    {
        path: "/redirecting",
        alias: "/redirigiendo",
        name: "Redirecting",
        component: () => import(/* webpackChunkName: "users" */ "../views/Redirecting.vue"),
        meta: {
            authenticatedAs: "candidate"
        },
        props: route => ({
            redirect: route.params.redirect,
            offerId: route.params.offerId
        }),
        beforeEnter(routeTo, routeFrom, next) {
            let entry = entryCache.get();

            if (entry && entry.params.redirect) {
                routeTo.params.redirect = entry.params.redirect;
                routeTo.params.offerId = entry.params.offerId;
                entryCache.clear();
            }
            next();
        }
    },
    {
        path: "/organization/pricing",
        alias: "/organizacion/planes",
        name: "OrganizationPricing",
        component: () => import(/* webpackChunkName: "users" */ "../views/OrganizationPricing.vue")
    },
    {
        path: "/redirect",
        alias: "/redireccion",
        name: "RedirectionRule",
        props: true,
        beforeEnter(routeTo, routeFrom, next) {
            let userType = store.getters["users/getType"];
            let userRole = store.getters["users/getRole"];
            let path = "/" + (userRole !== "sysadmin" ? userType : userRole) + "/home";
            let entry = entryCache.get();

            if (entry) {
                console.log(entry);
                if (entry.url) path = entry.url;
                entryCache.clear();
                if (!path) {
                    if (entry.params.redirect && entry.params.redirect != "") {
                        window.location.replace(entry.params.redirect);
                        path = false;
                    }
                    if (entry.query.redirect && entry.query.redirect != "") {
                        window.location.replace(entry.query.redirect);
                        path = false;
                    }
                }
            }

            entryCache.set({
                params: entry && entry.params ? entry.params : {},
                query: entry && entry.query ? entry.query : {}
            });

            next(path);
        }
    }
];

const router = new VueRouter({
    mode: "history",
    base: process.env.BASE_URL,
    routes,
    scrollBehavior() {
        return { x: 0, y: 0 };
    }
});

router.beforeEach((to, from, next) => {
    // Obtengo tipo y rol de usuario
    let userType = store.getters["users/getType"];
    let userRole = store.getters["users/getRole"];

    /// TODO si accede a login , estan registrado, mandalo a HOME //
    /// Este bloque verifica rutas protegidas, para enviar a login en caso de no estar registrado
    if (to.meta.authenticatedAs && !to.meta.roleAs) {
        if (!userType) {
            entryCache.set({
                url: to.path,
                params: to.params ? to.params : {},
                query: to.query ? to.query : {}
            });

            if (to.query.access) {
                next(to.query.access);
            } else {
                next({
                    name: "Register",
                    replace: true,
                    params: {
                        type: to.meta.authenticatedAs
                    }
                });
            }
        } else if (userType != to.meta.authenticatedAs) {
            console.warn("[warning] - intentando acceder a ruta con tipo no permitido");
            /// TODO Que hacer en caso que el usuario ya este registrado como otro tipo ????
            next("/redirect");
        } else {
            next();
        }
    } else if (to.meta.roleAs) {
        if (!userRole) {
            entryCache.set({
                url: to.path,
                params: to.params ? to.params : {},
                query: to.query ? to.query : {}
            });
            next(to.query.access ? to.query.access : "/login");
        } else if (to.meta.roleAs != "*" && userRole != to.meta.roleAs) {
            console.warn("[warning] - intentando acceder a ruta con rol no permitido");
            /// TODO Que hacer en caso que el usuario ya este registrado como otro tipo ????
            next("/redirect");
        } else {
            next();
        }
    } else {
        next();
    }
});

router.afterEach((to, from) => {
    store.dispatch("cookies/activity", {
        path: to.path,
        activity: "pageLoad"
    });
});

export default router;
